import TitleSection from "@/components/common/TitleSection"
import { TempLayout } from "@/layouts"
import { usaSapAssetStore } from "@/stores/sap-asset/sapAssetStore"
import { IGetEmployeeByIdParams } from "@/types"
import { useCallback, useEffect, useState, useRef } from "react"
import { useUserStore } from "@/stores/user/userStore"
import { ISapAsset } from "@/types/sapAsset.type"
import AssetCard from "./components/AssetCard"
import { useNavigate } from "react-router"
import SapApprovalStatusFilter from "@/features/shared/components/SapApprovalStatusFilter"

const SapAssets = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const { assets, meta, isLoading, fetchSapAssets } = usaSapAssetStore()
  const [allAssets, setAllAssets] = useState<ISapAsset[]>([])
  const prevDataLengthRef = useRef(0)
  const [params, setParams] = useState<IGetEmployeeByIdParams>({
    page: 1,
    per_page: 4,
    employee_id: "",
  })

  const handleRefetch = useCallback(async () => {
    if (params.employee_id) {
      await fetchSapAssets(params)
    }
  }, [fetchSapAssets, params])

  const handleReset = () => {
    setAllAssets([])
    prevDataLengthRef.current = 0
    setParams(prev => ({ ...prev, page: 1, approval_status: undefined }))
  }

  const handleAddAsset = () => {
    navigate("/sapasst/create")
  }

  const handleApprovalStatusChange = (approvalStatus?: string) => {
    setAllAssets([])
    prevDataLengthRef.current = 0
    setParams(prev => ({
      ...prev,
      page: 1,
      approval_status: approvalStatus || undefined,
    }))
  }

  useEffect(() => {
    if (userData.employee_id) {
      setParams(prev => ({
        ...prev,
        employee_id: userData.employee_id,
      }))
    }
  }, [userData.employee_id])

  useEffect(() => {
    if (params.employee_id) {
      void fetchSapAssets(params)
    }
  }, [fetchSapAssets, params, userData.employee_id])

  useEffect(() => {
    if (params.page === 1) {
      setAllAssets(assets)
      prevDataLengthRef.current = assets.length
    } else if (assets.length !== prevDataLengthRef.current) {
      setAllAssets(prev => {
        const existingIds = new Set(prev.map(a => a.record_id))
        const newAssets = assets.filter(a => !existingIds.has(a.record_id))
        return [...prev, ...newAssets]
      })
      prevDataLengthRef.current = assets.length
    }
  }, [assets, params.page])

  const handleLoadMore = () => {
    if (meta && params.page < meta.total_pages) {
      setParams(prev => ({
        ...prev,
        page: prev.page + 1,
      }))
    }
  }

  return (
    <TempLayout title="SAP - Assets" navNavigateUrl="/home">
      {/* Header */}
      <TitleSection title="SAP - Assets" onAdd={handleAddAsset} onRefresh={() => void handleRefetch()} />
      <SapApprovalStatusFilter value={params.approval_status} onChange={handleApprovalStatusChange} />

      <AssetCard assets={allAssets} isLoading={isLoading || !meta} handleReset={handleReset} onLoadMore={handleLoadMore} hasMore={meta ? params.page < meta.total_pages : true} />
    </TempLayout>
  )
}

export default SapAssets
